<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  pipelineOptions: {
    type: Array,
    required: true,
  },
  selectedPipelineId: {
    type: [String, Number],
    required: true,
  },
  dateRangeOptions: {
    type: Array,
    required: true,
  },
  dateRange: {
    type: String,
    required: true,
  },
  selectedDateLabel: {
    type: String,
    required: true,
  },
  isAllPipelinesMode: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['selectPipeline', 'selectDateRange']);

const { t } = useI18n();
const router = useRouter();

const showPipelineDropdown = ref(false);
const showDateDropdown = ref(false);

const selectedPipelineName = computed(() => {
  const pipeline = props.pipelineOptions.find(
    p => String(p.id) === String(props.selectedPipelineId)
  );
  return pipeline?.name || t('PIPELINE.UNIFIED_DASHBOARD.ALL_PIPELINES');
});

const handleBack = () => {
  router.push({ name: 'pipeline_index' });
};

const togglePipelineDropdown = () => {
  showPipelineDropdown.value = !showPipelineDropdown.value;
  showDateDropdown.value = false;
};

const toggleDateDropdown = () => {
  showDateDropdown.value = !showDateDropdown.value;
  showPipelineDropdown.value = false;
};

const handleSelectPipeline = pipeline => {
  emit('selectPipeline', pipeline.id);
  showPipelineDropdown.value = false;
};

const handleSelectDateRange = option => {
  emit('selectDateRange', option.value);
  showDateDropdown.value = false;
};

const closeDropdowns = () => {
  showPipelineDropdown.value = false;
  showDateDropdown.value = false;
};
</script>

<template>
  <header
    class="relative z-50 flex items-center justify-between h-12 px-3 border-b border-n-weak bg-n-background"
  >
    <!-- Left: Back + Title -->
    <div class="flex items-center gap-2 min-w-0">
      <button
        type="button"
        class="p-1.5 rounded-lg hover:bg-n-alpha-1 text-n-slate-10 hover:text-n-slate-12 transition-colors"
        :title="$t('GENERAL.BACK')"
        @click="handleBack"
      >
        <Icon icon="i-lucide-arrow-left" class="size-3.5" />
      </button>
      <h1 class="text-sm font-semibold text-n-slate-12 truncate">
        {{ $t('PIPELINE.UNIFIED_DASHBOARD.TITLE') }}
      </h1>

      <div class="hidden md:block w-px h-4 bg-n-weak mx-1" />

      <span class="hidden md:inline text-[11px] text-n-slate-10">
        {{
          isAllPipelinesMode
            ? $t('PIPELINE.UNIFIED_DASHBOARD.SUBTITLE_ALL')
            : $t('PIPELINE.UNIFIED_DASHBOARD.SUBTITLE_SINGLE')
        }}
      </span>
    </div>

    <!-- Right: Filters -->
    <div class="flex items-center gap-1.5">
      <!-- Pipeline Selector -->
      <div class="relative">
        <button
          type="button"
          class="flex items-center gap-1.5 h-7 px-2.5 rounded-lg text-[11px] font-medium text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1 transition-colors"
          @click="togglePipelineDropdown"
        >
          <Icon
            :icon="
              isAllPipelinesMode
                ? 'i-lucide-layers'
                : 'i-lucide-git-branch-plus'
            "
            class="size-3.5"
          />
          <span class="truncate max-w-[140px]">
            {{ selectedPipelineName }}
          </span>
          <Icon
            icon="i-lucide-chevron-down"
            class="size-3 transition-transform"
            :class="{ 'rotate-180': showPipelineDropdown }"
          />
        </button>
        <Transition
          enter-active-class="transition duration-100 ease-out"
          enter-from-class="opacity-0 scale-95"
          enter-to-class="opacity-100 scale-100"
          leave-active-class="transition duration-75 ease-in"
          leave-from-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-95"
        >
          <div
            v-if="showPipelineDropdown"
            class="absolute right-0 top-full mt-1 z-10 min-w-[200px] rounded-lg border border-n-weak bg-n-solid-2 shadow-lg py-1 origin-top-right"
          >
            <button
              v-for="pipeline in pipelineOptions"
              :key="pipeline.id"
              type="button"
              class="w-full px-3 py-1.5 text-left text-[11px] text-n-slate-12 hover:bg-n-alpha-2 transition-colors flex items-center gap-2"
              :class="{
                'bg-n-alpha-2 font-semibold':
                  String(selectedPipelineId) === String(pipeline.id),
              }"
              @click="handleSelectPipeline(pipeline)"
            >
              <Icon
                :icon="
                  pipeline.id === 'all'
                    ? 'i-lucide-layers'
                    : 'i-lucide-git-branch-plus'
                "
                class="size-3.5 text-n-slate-9"
              />
              {{ pipeline.name }}
            </button>
          </div>
        </Transition>
      </div>

      <div class="w-px h-4 bg-n-weak" />

      <!-- Date Range Selector -->
      <div class="relative">
        <button
          type="button"
          class="flex items-center gap-1.5 h-7 px-2.5 rounded-lg text-[11px] font-medium text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1 transition-colors"
          @click="toggleDateDropdown"
        >
          <Icon icon="i-lucide-calendar" class="size-3.5" />
          <span class="truncate max-w-[120px]">{{ selectedDateLabel }}</span>
          <Icon
            icon="i-lucide-chevron-down"
            class="size-3 transition-transform"
            :class="{ 'rotate-180': showDateDropdown }"
          />
        </button>
        <Transition
          enter-active-class="transition duration-100 ease-out"
          enter-from-class="opacity-0 scale-95"
          enter-to-class="opacity-100 scale-100"
          leave-active-class="transition duration-75 ease-in"
          leave-from-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-95"
        >
          <div
            v-if="showDateDropdown"
            class="absolute right-0 top-full mt-1 z-10 min-w-[160px] rounded-lg border border-n-weak bg-n-solid-2 shadow-lg py-1 origin-top-right"
          >
            <button
              v-for="option in dateRangeOptions"
              :key="option.value"
              type="button"
              class="w-full px-3 py-1.5 text-left text-[11px] text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
              :class="{
                'bg-n-alpha-2 font-semibold': dateRange === option.value,
              }"
              @click="handleSelectDateRange(option)"
            >
              {{ option.label }}
            </button>
          </div>
        </Transition>
      </div>
    </div>

    <!-- Click outside to close dropdowns -->
    <div
      v-if="showPipelineDropdown || showDateDropdown"
      class="fixed inset-0 z-0"
      @click="closeDropdowns"
    />
  </header>
</template>
